import React from 'react';
import { useLazyListMessagesQuery } from 'api/metaWhatsappApi';

/**
 * Pages of history before the newest page, loaded as the reader scrolls up.
 *
 * ★ THE HEAD PAGE STAYS A NORMAL QUERY. `useListMessagesQuery({ conversationId })`
 * is what the socket invalidates and refetches on every new message and tick;
 * it must keep its argument shape. RTK Query's `merge`/`serializeQueryArgs`
 * pagination would make the refetch run with the LAST argument — a `before`
 * cursor — and drop the newest page. So older pages are fetched lazily and
 * kept in component state instead, and only the head page is live.
 *
 * Pages are newest-first as the server sends them; the view flattens and
 * reverses. The cursor of the last page decides whether there is more.
 */
export default function useOlderMessages({ conversationId, headCursor }) {
	const [pages, setPages] = React.useState([]);
	const [cursor, setCursor] = React.useState(undefined); // undefined = not started
	const [done, setDone] = React.useState(false);
	const [trigger, { isFetching }] = useLazyListMessagesQuery();
	const inFlight = React.useRef(false);

	React.useEffect(() => {
		setPages([]);
		setCursor(undefined);
		setDone(false);
		inFlight.current = false;
	}, [conversationId]);

	const next = cursor === undefined ? headCursor ?? null : cursor;
	const hasMore = !done && Boolean(next);

	/**
	 * @returns {Promise<boolean>} whether a page actually landed.
	 *
	 * ★ THE CALLER NEEDS THE ANSWER, NOT JUST THE SIDE EFFECT. The view captures
	 * a scroll anchor immediately before asking for a page, and applies it when
	 * the page arrives. If the request never ran — a duplicate suppressed here,
	 * nothing left to fetch — or it failed, no page arrives, nothing consumes
	 * that anchor, and the guard against overwriting an unapplied anchor would
	 * then refuse every later page. Reporting back lets the view drop it.
	 */
	const loadOlder = React.useCallback(async () => {
		if (!conversationId || !hasMore || inFlight.current) return false;
		inFlight.current = true;
		try {
			const result = await trigger({ conversationId, before: next }, true).unwrap();
			setPages((p) => [...p, result?.messages ?? []]);
			if (result?.nextCursor) setCursor(result.nextCursor);
			else {
				setCursor(null);
				setDone(true);
			}
			return true;
		} catch (_) {
			// A failed page is not fatal: the reader can scroll again.
			return false;
		} finally {
			inFlight.current = false;
		}
	}, [conversationId, hasMore, next, trigger]);

	const older = React.useMemo(() => pages.flat(), [pages]);

	return { older, loadOlder, isFetching, hasMore };
}
